import React, { useState } from "react";
import ParseoLogo, { ParseoSpinner } from "../../shared/ui/ParseoLogo";

interface PatientLoginProps {
  onLogin: (session: { token: string; email: string }) => void;
}

export default function PatientLogin({ onLogin }: PatientLoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) return;
    setError("");
    setMessage("");
    setLoading(true);

    const endpoint = isSignup ? "/api/auth/patient/signup" : "/api/auth/patient/login";
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: password || undefined }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.token) {
          onLogin({ token: data.token, email });
        } else {
          setMessage(data.message || "Check your email for a login link.");
        }
      } else {
        setError(data.detail || data.error || "Authentication failed");
      }
    } catch {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: "#f5f0eb", padding: 20,
    }}>
      <div style={{
        backgroundColor: "white", borderRadius: 16, padding: "40px 36px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)", maxWidth: 400, width: "100%",
      }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <ParseoLogo size={44} />
            <div style={{ textAlign: "left" }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: "#182a33", margin: 0, lineHeight: 1.15 }}>
                {isSignup ? "Create your account" : "Patient Portal"}
              </h1>
              <p style={{ fontSize: 13, color: "#5b6e7c", margin: 0, lineHeight: 1.2 }}>
                Parseo Health
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 4 }}>
              Email
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              required autoFocus
              style={{
                width: "100%", padding: "10px 14px", fontSize: 15, borderRadius: 8,
                border: "1px solid #d8c9b6", outline: "none", boxSizing: "border-box",
              }}
              placeholder="you@example.com"
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 4 }}>
              Password
            </label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px", fontSize: 15, borderRadius: 8,
                border: "1px solid #d8c9b6", outline: "none", boxSizing: "border-box",
              }}
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div style={{
              backgroundColor: "#faeeed", border: "1px solid #f3d5d3", borderRadius: 8,
              padding: "8px 12px", marginBottom: 16, fontSize: 13, color: "#c25450",
            }}>
              {error}
            </div>
          )}

          {message && (
            <div style={{
              backgroundColor: "#eef4f2", border: "1px solid #bbd5cc", borderRadius: 8,
              padding: "8px 12px", marginBottom: 16, fontSize: 13, color: "#6b9c8e",
            }}>
              {message}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              width: "100%", padding: "12px 0", fontSize: 16, fontWeight: 600,
              borderRadius: 8, border: "none", cursor: loading ? "wait" : "pointer",
              backgroundColor: "#182a33", color: "white",
            }}
          >
            {loading ? (<span style={{display:'inline-flex',alignItems:'center',gap:8}}><ParseoSpinner size={16} color="#ffffff"/> ...</span>) : isSignup ? "Create Account" : "Sign In"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button
            onClick={() => { setIsSignup(!isSignup); setError(""); setMessage(""); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "#182a33", textDecoration: "underline",
            }}
          >
            {isSignup ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
        </div>
      </div>
    </div>
  );
}
